import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, Keys, register } from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './modal.lit.scss';

/**
 * @component {kat-modal} KatalModal Modal dialogs interrupt users and demand an action. They are appropriate when userâ€™s attention needs to be directed toward important information
 * @contentProvided
 * @slot default Elements without a named slot attribute; appear inside the main modal body
 * @slot title Title element; appears inside the modal header. Takes precedence over title property
 * @slot footer Contents will be used as the modal footer. Add 'kat-group-horizontal' on footer element to automatically space its children
 * @example Basic {"visible": false, "content": "<span slot=\"title\">Modal Title</span><p>Lorem ipsum dolor sit amet, sit nihil utamur ne, alia persequeris sit id, usu ut erant quando. Primis deleniti scribentur sit ex.</p><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p><div slot=\"footer\" class=\"kat-group-horizontal\"><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></div>"}
 * @example noCloseIcon {"visible":false, "no-close-icon": true, "title": "Modal With No Close Icon", "content":"<p>Lorem ipsum dolor sit amet, sit nihil utamur ne, alia persequeris sit id, usu ut erant quando. Primis deleniti scribentur sit ex.</p><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p><div slot=\"footer\" class=\"kat-group-horizontal\"><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></div>"}
 * @example ScrollFixed { "visible": false, "content":"<span slot=\"title\">Scroll Modal Body</span><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p><kat-box variant=\"white-junglemist\" ><p>Modal has a fixed header and footer... keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>You've reached the end!</p></kat-box><div slot=\"footer\" class=\"kat-group-horizontal\"><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></div>"}
 * @example ScrollAll { "visible": false, "scroll-all": true, "content":"<span slot=\"title\">Scroll Entire Modal</span><p>The buttons in this example will not perform any actions.  It is up the the application's logic to decide what happens when buttons are added to a Modal</p><kat-box variant=\"white-junglemist\" ><p>Modal scrolls along with the page... keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>keep scrolling</p><p>You've reached the end!</p></kat-box><div slot=\"footer\" class=\"kat-group-horizontal\"><kat-button variant=\"secondary\" label=\"Cancel\"></kat-button><kat-button label=\"Submit\"></kat-button></div>"}
 * @event open Fires when the modal dialog is opened
 * @event close Fired when the modal dialog is closed
 * @status Production
 * @theme flo
 * @guideline Do Modals can work effectively when the information being requested or presented is relevant or can streamline the completion of the current task.
 * @guideline Dont Do not use modal dialogs for nonessential information that is not related to the current user flow.
 */
@register('kat-modal')
export class KatalModal extends KatLitElement {
  /**
   * @classprop {boolean} visible determines if the dialog is open or closed.
   */
  @property()
  visible: boolean;

  /**
   * @classprop {string} title Title text that will be displayed to the user. NOTE: title slot takes precedence over title property
   */
  @property()
  title: string;

  /**
   * @classprop {boolean} no-close-icon Determines whether to show or hide the header's close icon
   */
  @property({ attribute: 'no-close-icon' })
  noCloseIcon: boolean;

  /**
   * @classprop {boolean} scroll-all Scroll the entirety of the modal along the page (no fixed header + footer). Defaults to false
   */
  @property({ attribute: 'scroll-all' })
  scrollAll: boolean;

  static get styles() {
    return [baseStyles, styles];
  }

  constructor() {
    super();
    this._clickStartedOnModal = false;
  }

  _handleKeyPressEvent(event) {
    if (!!this.visible && event.keyCode === Keys.Escape) {
      this.close();
    }
  }

  _handleMouseDown(event) {
    this._clickStartedOnModal = event
      .composedPath()
      .includes(this._dialogTarget);
  }

  _handleMouseUp(event) {
    if (
      !this._clickStartedOnModal &&
      !event.composedPath().includes(this._dialogTarget)
    ) {
      this.close();
    }

    this._clickStartedOnModal = false;
  }

  /**
   * Call to close the modal. Fires the close event.
   *
   * @katalmethod
   */
  async close() {
    const canceled = !this.dispatchCustomEvent(
      'close',
      { visible: false },
      { bubbles: false, cancelable: true }
    );

    if (canceled) {
      return;
    }

    this.visible = false;
    await this.updateComplete;
  }

  /**
   * Call to open the modal. Fires the open event.
   *
   * @katalmethod
   */
  async open() {
    const canceled = !this.dispatchCustomEvent(
      'open',
      { visible: true },
      {
        bubbles: false,
        cancelable: true,
      }
    );

    if (canceled) {
      return;
    }

    this.visible = true;
    await this.updateComplete;
  }

  _childrenChanged(e) {
    this.requestUpdate();
  }

  firstUpdated() {
    this.setAttribute('aria-role', 'dialog');
    this.setAttribute('aria-modal', true);
    this.setAttribute('aria-labelledby', 'title');

    this._dialogTarget = this.shadowRoot.querySelector('.dialog');
    document.addEventListener('keydown', e => this._handleKeyPressEvent(e), {
      capture: true,
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._handleKeyPressEvent, true);
  }

  updated(changed) {
    if (changed.has('visible')) {
      document.body.classList.toggle('kat-no-scroll', !!this.visible);
    }
  }

  render() {
    const result = checkSlots(this);
    const header = this.title || result.title || !this.noCloseIcon;
    return html`
      <div
        class="container"
        @mousedown=${this._handleMouseDown}
        @mouseup=${this._handleMouseUp}
      >
        <div class="dialog">
          ${header
            ? html`
                <div class="header" part="modal-header">
                  <div class="title" id="title">
                    <slot name="title" @slotchange=${this._childrenChanged}
                      >${this.title
                        ? html`
                            <span class="title__inner">${this.title}</span>
                          `
                        : nothing}</slot
                    >
                  </div>
                  ${this.noCloseIcon
                    ? nothing
                    : html`
                        <button
                          class="close"
                          type="button"
                          @click=${this.close}
                          part="modal-close-button"
                        >
                          <kat-icon
                            class="close__icon"
                            part="modal-close-icon"
                            name="exit"
                          ></kat-icon>
                        </button>
                      `}
                </div>
              `
            : nothing}
          <div class="body">
            <slot></slot>
          </div>
          <div class="footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    `;
  }
}
